import { useCallback, useEffect, useState } from "react"
import useScenario from "./useScenario"
import CommandCenterService from "../../../service/CommandCenterService"
import useUrlParams from "./useUrlParams"
import useTrueTwin from "./useTrueTwin"
import { showToastMessage } from "../../../utils/commonFunctions"
import { get_monitor_button_list } from "../components/center/monitor/utils"

const useMonitor = () => {
    const { set_telemetry_time, telemetry_time, grafana, scenario } = useScenario()
    const { scenario_id, dashboard } = useUrlParams()
    const { truetwin_id } = useTrueTwin()
    const [selected_panel, set_selected_panel] = useState<string>('Beacon')
    const [monitor_view_type, set_monitor_view_type] = useState<string>('Grid')
    const [grafana_data, set_grafana_data] = useState<any>({})
    const [panels, set_panels] = useState<any | null>(null)


    useEffect(() => {
        if (grafana) {
            const grafana_data = grafana?.[0]
            set_grafana_data(grafana_data)
            
            const allowedButtons = get_monitor_button_list(scenario, dashboard)
            const initialPanel = allowedButtons.length > 0 ? allowedButtons[0].value : 'EPS'
            const finalSelected = !allowedButtons.some(b => b.value === selected_panel)
                ? initialPanel 
                : selected_panel;

            set_panels(grafana_data?.[finalSelected === "Communication" ? 'Comms' : finalSelected])
            set_selected_panel(finalSelected)
            change_monitor_view_type('Grid')
        }
    }, [grafana, scenario, dashboard])

    const [fileName, setFileName] = useState('');


    const handleFileUpload = () => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '.txt'; // Accept only .txt files
        fileInput.onchange = (event: any) => {
            const file = event.target.files[0];
            if (file && file.type === 'text/plain') {
                const reader = new FileReader();
                reader.onload = (e: any) => {
                    if (!e) return;
                    const uploadFile = e.target.result;
                    const blob = new Blob([uploadFile], { type: "multipart/form-data" });
                    const file_data = new FormData();
                    file_data.append("file", blob, file.name);
                    file_data.append("scenarioId", scenario_id as string);
                    file_data.append("trueTwinId", truetwin_id as string);
                    file_data.append("bandType", "Backdoor");
                    // Call the upload API with the FormData object
                    setFileName(file.name);
                    uploadAPI(file_data);
                };
                reader.readAsText(file);
            } else {
                console.error('Invalid file format');
                showToastMessage('error', 'Invalid file format. Please upload a .txt file.');
            }
        };
        fileInput.click();
    };

    const uploadAPI = (file_data) => {
        CommandCenterService.postUploadFileConsoleFlatSatTele(file_data)
            .then((response) => {
                if (response) {
                    setFileName('')
                    const message = response?.data?.message;
                    showToastMessage('success', message || 'File uploaded successfully');
                } else {
                    showToastMessage('error', 'File upload failed');
                }
            })
            .catch((error) => {
                const message = error?.response?.data?.message;
                showToastMessage('error', message || 'Error uploading file. Please try again.');
            })
            .finally(() => {
                console.log('Upload process finished.');
            });
    };

    const change_selected_panel = (panel: string) => {
        if (panel === 'Communication') {
            set_panels(grafana_data?.['Comms'])
            set_selected_panel(panel)
            return
        } else if (panel === 'Payload') {
            set_panels(grafana_data?.['PAYLOAD']?.['PayloadApp'] || [])
            set_selected_panel(panel)
            return
        } else {
            set_panels(grafana_data?.[panel])
            set_selected_panel(panel)
            return
        }
    }

    const change_monitor_view_type = (view: string) => {
        set_monitor_view_type(view)
    }

    function updateUrlsWithTimeRange(url: string, from: number | string, to: number | string) {
        const updatedUrl = url.split('from')[0]
        return `${updatedUrl}&from=${from}&to=${to}`; // Create a new object with the updated URL
    }

    const get_updated_url = useCallback((url: string) => {
        if (telemetry_time && telemetry_time[0] && telemetry_time[1]) {
            const from = (new Date(telemetry_time[0])?.getTime())*1000
            const to = (new Date(telemetry_time[1])?.getTime())*1000
            if (from && to && (from > to)) {
                showToastMessage('error', 'Start time cannot be greater than End time')
                return url
            } else {
                const updated_url = updateUrlsWithTimeRange(url, from, to)
                return updated_url
            }
        } else {
            return url
        }
    }, [telemetry_time])

    return {
        panels,
        selected_panel,
        telemetry_time,
        get_updated_url,
        set_telemetry_time,
        change_selected_panel,
        monitor_view_type,
        change_monitor_view_type,
        handleFileUpload,
        fileName,
    }
}

export default useMonitor